/*
 * Swaps the main blog section in place when a filter, pagination or empty-state
 * link is clicked, instead of reloading the page.
 *
 * The links stay real hrefs to Shopify's /tagged/ URLs, so the Liquid still owns
 * every filter state. This only fetches that URL through the Section Rendering
 * API and replaces #MainBlog with the result. Any failure falls back to a normal
 * page load.
 */
(() => {
  // The theme editor can re-render the section and emit this tag again.
  if (window.jgBlogFilterLoaded) return;
  window.jgBlogFilterLoaded = true;

  const ROOT_ID = 'MainBlog';
  const LINK_SELECTOR = ['.blog-filter a', '.pagination a', '.blog-empty a', 'a.article-card__category']
    .map((selector) => `#${ROOT_ID} ${selector}`)
    .join(', ');

  const TOGGLE_SELECTOR = `#${ROOT_ID} .toggle-filter-btn`;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  let controller;
  // Kept outside the DOM because every swap renders the filter fresh, open.
  let topicsHidden = false;

  // A swap applies the state as is; only a click animates. is-animating clips
  // the row while it moves and comes off at the end, so the More dropdown can
  // overflow the open row again.
  function applyTopicsToggle(root, animate = false) {
    const button = root.querySelector('.toggle-filter-btn');
    const topics = button && document.getElementById(button.getAttribute('aria-controls'));
    if (!topics) return;

    if (animate && !reducedMotion.matches) {
      topics.classList.add('is-animating');
      const done = (event) => {
        if (event.target !== topics || event.propertyName !== 'grid-template-rows') return;
        topics.classList.remove('is-animating');
        topics.removeEventListener('transitionend', done);
      };
      topics.addEventListener('transitionend', done);
    }

    topics.classList.toggle('is-collapsed', topicsHidden);
    button.setAttribute('aria-expanded', String(!topicsHidden));
    button.querySelector('.toggle-filter-btn__label').textContent = topicsHidden
      ? button.dataset.showLabel
      : button.dataset.hideLabel;
  }

  // Path and query of what the section currently shows. A hash-only change
  // (a skip link, #MainBlog) also fires popstate and must not refetch.
  let currentPath = window.location.pathname + window.location.search;

  // Scripts set through innerHTML never run. A card can emit one the first page
  // did not (video-duration.js when the first page had no videos), so load any
  // that are new; ones already on the page are just asked to rescan.
  function runScripts(root) {
    root.querySelectorAll('script[src]').forEach((script) => {
      const loaded = [...document.scripts].some((existing) => !root.contains(existing) && existing.src === script.src);
      if (loaded) return;
      const clone = document.createElement('script');
      clone.src = script.src;
      clone.defer = true;
      document.head.appendChild(clone);
    });

    window.jgVideoDuration?.refresh(root);
  }

  async function load(url, push) {
    const root = document.getElementById(ROOT_ID);
    if (!root || !root.dataset.sectionId) {
      window.location.assign(url);
      return;
    }

    controller?.abort();
    const request = new AbortController();
    controller = request;

    const target = new URL(url, window.location.origin);
    target.hash = '';
    const targetPath = target.pathname + target.search;
    const fetchUrl = new URL(target);
    fetchUrl.searchParams.set('section_id', root.dataset.sectionId);

    root.classList.add('is-loading');
    root.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(fetchUrl, { signal: request.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.getElementById(ROOT_ID);
      if (!fresh) throw new Error('Blog section missing from response');

      root.innerHTML = fresh.innerHTML;
      currentPath = targetPath;
      if (push) window.history.pushState({ blogFilter: true }, '', targetPath);

      applyTopicsToggle(root);
      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(root);
      runScripts(root);

      // The clicked link is gone, so hand focus to the section. Only scroll when
      // its top has moved out of view, e.g. after a pagination click at the bottom.
      root.setAttribute('tabindex', '-1');
      root.focus({ preventScroll: true });
      if (root.getBoundingClientRect().top < 0) root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      if (error.name !== 'AbortError') window.location.assign(url);
    } finally {
      // An aborted request must not clear the loading state of the one replacing it.
      if (controller === request) {
        root.classList.remove('is-loading');
        root.removeAttribute('aria-busy');
      }
    }
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest(TOGGLE_SELECTOR)) {
      topicsHidden = !topicsHidden;
      applyTopicsToggle(document.getElementById(ROOT_ID), true);
      return;
    }

    const link = event.target.closest(LINK_SELECTOR);
    if (!link || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.origin !== window.location.origin) return;

    event.preventDefault();
    load(link.href, true);
  });

  window.addEventListener('popstate', () => {
    if (!document.getElementById(ROOT_ID)) return;
    if (window.location.pathname + window.location.search === currentPath) return;
    load(window.location.href, false);
  });
})();
